/**
 * Single source of truth for the custom-role permission catalog.
 *
 * Consumed by the web app's role permission picker. Each UI toggle maps to one
 * or more low-level permission rules (`action` × `resource` × `subject`) that
 * the API resolves against seeded ALLOW `Permission` rows on role
 * create/update, so the picker and the DB never drift.
 *
 * Tier read visibility is fully toggle-controlled: the organization.view /
 * teams.view / projects.view toggles grant bare read rows on their tier, and
 * the API stores exactly what the picker sends — no server-side baseline is
 * unioned in. A role without projects.view serializes project rows at the
 * nav-floor field limit (id/name/scopeId/createdAt) for its holders.
 */

export type PermissionAction =
  | "manage"
  | "create"
  | "read"
  | "update"
  | "delete"
  | "upload"
  | "download"
  | "share"
  | "approve"
  | "reject"
  | "invite"
  | "assign"
  | "comment"
  | "export"
  | "import"
  | "move"
  | "editOrgSettings"
  | "editTeamSettings"
  | "editProjectSettings"
  | "archive";

/** Resource a rule targets — matches the API `Permission.resource` (ScopeType) column. */
export type PermissionResource =
  | "ORGANIZATION"
  | "TEAM"
  | "PROJECT"
  | "USER"
  | "FILE"
  | "THREAD"
  | "CLIENT"
  | "FOLDER"
  | "PROJECT_TYPE"
  | "OWNER"
  | "ARCHITECT"
  | "DEPARTMENT"
  | "REGION";

/** Reviews vs. playbooks are the same entity split by template type; carried here so the two are toggleable independently. */
export type ChecklistTemplateType = "CHECKLIST" | "PLAYBOOK";

/**
 * Subject on a review/playbook rule. FILE-hosted rows carry a FILE_-prefixed
 * subject: /me serializes ability rules without the resource column, and
 * PROJECT-resource rules cascade over a superset of the FILE rows' scopes, so
 * identical subjects would make document-level and project-level grants
 * indistinguishable to the web app.
 */
export type ChecklistTemplateSubject = ChecklistTemplateType | `FILE_${ChecklistTemplateType}`;

/** Subject for comment/markup rules — one subject per concept, independently grantable across every commentable/markup-able object type (organization, team, project, and file tiers). */
export type AnnotationSubject = "COMMENT" | "MARKUP";

/** Subject for redlining rules — FILE-hosted only, FILE_-prefixed for the same /me serialization reason as ChecklistTemplateSubject. */
export type RedlineSubject = "FILE_REDLINE";

/** Subjects for change order rules — potential change orders, their integration source records, and the ProjectSight connection. */
export type ChangeOrderSubject = "POTENTIAL_CHANGE_ORDER" | "SOURCE_RECORD" | "INTEGRATION";

/** Subject for workflow rules — templates and applications each split into an entity and its steps. */
export type WorkflowSubject =
  | "WORKFLOW_TEMPLATE"
  | "WORKFLOW_TEMPLATE_STEP"
  | "WORKFLOW_APPLICATION"
  | "WORKFLOW_APPLICATION_STEP";

/**
 * A single grant, aligned to the API `Permission` row it resolves to
 * (`action` × `resource` × optional `subject`). The server links the matching
 * ALLOW permission; `subject` defaults to `resource` server-side when omitted.
 */
export interface PermissionRule {
  action: PermissionAction;
  resource: PermissionResource;
  /** Set only when the Permission row's subject differs from the resource (e.g. checklist template subject, comment/markup subject, workflow subject, "USER" for tier-scoped user management rows, "ROLE" for custom-role management, or "TASK" for org-wide task management). */
  subject?:
    | ChecklistTemplateSubject
    | AnnotationSubject
    | WorkflowSubject
    | RedlineSubject
    | ChangeOrderSubject
    | "USER"
    | "ROLE"
    | "TASK"
    | "SKILL"
    | "SKILL_OVERRIDE"
    | "ORG_TEMPLATE";
}

export interface PermissionToggle {
  /** Stable identifier used as the picker key and to persist a role's selection. */
  key: string;
  /** Default English label; the web picker may localize via `key`. */
  label: string;
  /** One-line explanation shown under the label in the picker. */
  description: string;
  /** Low-level rules granted when this toggle is enabled. */
  rules: PermissionRule[];
  /**
   * Toggle key(s) this one depends on, possibly in another category. The picker forces them on
   * with this toggle, and clears this toggle when any of them goes off.
   */
  requires?: string | string[];
}

export interface PermissionSubgroup {
  subheading: string;
  toggleKeys: string[];
}

export interface PermissionGroup {
  subheading: string;
  toggleKeys?: string[];
  subgroups?: PermissionSubgroup[];
}

export interface PermissionCategory {
  key: string;
  /** Short label used in the section-index nav. */
  label: string;
  /** Section header shown in the gray box, e.g. "Organization Permissions". */
  heading: string;
  subheading?: string;
  /** One-line explanation of what the section's permissions govern. */
  description: string;
  groups?: PermissionGroup[];
  toggles: PermissionToggle[];
}

// Reviews/playbooks anchor to crunches (FILE scopes) and projects — the only
// resources their permission rows are seeded for.
const template = (
  resource: Extract<PermissionResource, "FILE" | "PROJECT">,
  action: PermissionAction,
  type: ChecklistTemplateType,
): PermissionRule => ({
  action,
  resource,
  subject: resource === "FILE" ? (`FILE_${type}` as const) : type,
});

// Custom roles are assignable at ORGANIZATION, TEAM, or PROJECT tier, and the
// API ability builder only honors rows whose resource tier is at or below the
// assignment tier (rows above it lose their write actions to read-only
// ascension) — so each task toggle carries one row per assignable tier.
const taskRules = (action: PermissionAction): PermissionRule[] =>
  (["ORGANIZATION", "TEAM", "PROJECT"] as const).map((resource) => ({
    action,
    resource,
    subject: "TASK" as const,
  }));

// One COMMENT subject governs commenting on every object type. Same
// per-assignable-tier convention as taskRules, plus FILE since document
// comments are hosted at the FILE tier (documents aren't a custom-role
// assignment tier, but the row is still needed for the ability check to
// match at that resource).
const commentRules = (action: PermissionAction): PermissionRule[] =>
  (["ORGANIZATION", "TEAM", "PROJECT", "FILE"] as const).map((resource) => ({
    action,
    resource,
    subject: "COMMENT" as const,
  }));

// Markup is FILE-only — there's no project-level markup concept.
const markupRules = (action: PermissionAction): PermissionRule[] => [
  { action, resource: "FILE", subject: "MARKUP" as const },
];

const changeOrderSubjectRules = (action: PermissionAction, subject: ChangeOrderSubject): PermissionRule[] =>
  (["ORGANIZATION", "TEAM", "PROJECT"] as const).map((resource) => ({
    action,
    resource,
    subject,
  }));

const changeOrderRules = (action: PermissionAction): PermissionRule[] =>
  changeOrderSubjectRules(action, "POTENTIAL_CHANGE_ORDER");

// User management rules follow the same pattern as tasks - one row per assignable tier
// When applied at a given level, permissions cascade down to child scopes
const userRules = (action: PermissionAction): PermissionRule[] =>
  (["ORGANIZATION", "TEAM", "PROJECT"] as const).map((resource) => ({
    action,
    resource,
    subject: "USER" as const,
  }));

// No PROJECT template row is seeded, so a PROJECT-assigned role gets read-only ascension.
const TEMPLATE_TIERS: PermissionResource[] = ["ORGANIZATION", "TEAM"];

// PROJECT cascades to FOLDER and FILE, so those tiers need no rows of their own.
const APPLICATION_TIERS: PermissionResource[] = ["ORGANIZATION", "TEAM", "PROJECT"];

type WorkflowGrant = [PermissionAction, WorkflowSubject];

const TEMPLATE_READS: WorkflowGrant[] = [
  ["read", "WORKFLOW_TEMPLATE"],
  ["read", "WORKFLOW_TEMPLATE_STEP"],
];

const APPLICATION_READS: WorkflowGrant[] = [
  ["read", "WORKFLOW_APPLICATION"],
  ["read", "WORKFLOW_APPLICATION_STEP"],
];

// Advancing, reopening, reassigning, and poking a step read and update that
// step's Tasks under RLS, and `include=steps.tasks` reads them, so an
// applied-workflow toggle is useless without both task toggles.
const TASK_DEPENDENCIES = ["tasks.view", "tasks.edit"];

// Removing a step or an application cascades into task.deleteMany, and that
// cascade reports success off its findMany rather than the delete's match
// count: without tasks.delete the tasks are silently orphaned under a
// soft-deleted step.
const TASK_DEPENDENCIES_WITH_DELETE = [...TASK_DEPENDENCIES, "tasks.delete"];

const workflowRules = (tiers: PermissionResource[], grants: WorkflowGrant[]): PermissionRule[] =>
  tiers.flatMap((resource) => grants.map(([action, subject]) => ({ action, resource, subject })));

const ORGANIZATION_SKILL_READ_RULES: PermissionRule[] = [
  { action: "read", resource: "ORGANIZATION", subject: "SKILL" },
  { action: "read", resource: "ORGANIZATION", subject: "SKILL_OVERRIDE" },
];

const ORG_TEMPLATE_READ_RULES: PermissionRule[] = [
  { action: "read", resource: "ORGANIZATION", subject: "ORG_TEMPLATE" },
];

const REVIEWS_PLAYBOOKS_TOGGLE_PREFIX = "organization.reviewsPlaybooks.";
const REVIEWS_PLAYBOOKS_SUBHEADING = "Customize Playbooks and Reviews";

export const PERMISSION_CATALOG: PermissionCategory[] = [
  {
    key: "users",
    label: "Users",
    heading: "User management",
    subheading: "User management",
    description: "Controls who can view and manage team members at each level.",
    toggles: [
      {
        key: "users.view",
        label: "View users",
        description: "Allows users to view users.",
        rules: userRules("read"),
      },
      {
        key: "users.manage",
        label: "Manage users",
        description: "Allows users to invite, edit, and remove users.",
        requires: "users.view",
        rules: [
          ...userRules("read"),
          // Invite permission must be on the scope resource itself (no subject) to match authorization check
          { action: "invite", resource: "ORGANIZATION" },
          { action: "invite", resource: "TEAM" },
          { action: "invite", resource: "PROJECT" },
          ...userRules("update"),
          ...userRules("delete"),
        ],
      },
    ],
  },
  {
    key: "organization",
    label: "Organization",
    heading: "Org level permissions",
    description: "Org-wide controls for settings, teams, and custom roles.",
    groups: [
      { subheading: "Org management", toggleKeys: ["organization.view", "organization.edit"] },
      { subheading: "Team management", toggleKeys: ["organization.createTeams", "organization.deleteTeams"] },
      { subheading: "Role management", toggleKeys: ["organization.manageCustomRoles"] },
      {
        subheading: "Organization skills",
        toggleKeys: [
          "organization.skills.viewAndUse",
          "organization.skills.createEdit",
          "organization.skills.delete",
          "organization.skills.manageAvailability",
        ],
      },
      {
        subheading: REVIEWS_PLAYBOOKS_SUBHEADING,
        toggleKeys: [
          "organization.reviewsPlaybooks.view",
          "organization.reviewsPlaybooks.createEdit",
          "organization.reviewsPlaybooks.delete",
        ],
      },
    ],
    toggles: [
      {
        key: "organization.view",
        label: "View organization",
        description: "Allows users to view the organization and everything in it",
        rules: [{ action: "read", resource: "ORGANIZATION" }],
      },
      {
        key: "organization.edit",
        label: "Edit organization settings",
        description: "Allows users to make changes to the organization name, settings, and templates.",
        rules: [
          { action: "update", resource: "ORGANIZATION" },
          { action: "editOrgSettings", resource: "ORGANIZATION" },
        ],
      },
      {
        key: "organization.createTeams",
        label: "Create teams",
        description: "Allows users to create new teams in an org.",
        rules: [{ action: "create", resource: "TEAM" }],
      },
      {
        key: "organization.deleteTeams",
        label: "Delete teams",
        description: "Allows users to delete teams.",
        rules: [{ action: "delete", resource: "TEAM" }],
      },
      {
        key: "organization.manageCustomRoles",
        label: "Manage custom roles",
        description: "Allows users to manage custom roles.",
        rules: [{ action: "manage", resource: "ORGANIZATION", subject: "ROLE" }],
      },
      {
        key: "organization.skills.viewAndUse",
        label: "View and use organization skills",
        description: "Allows users to view and use organization skills.",
        rules: ORGANIZATION_SKILL_READ_RULES,
      },
      {
        key: "organization.skills.createEdit",
        label: "Create and edit organization skills",
        description: "Allows users to create and edit organization skills.",
        requires: ["organization.skills.viewAndUse", "organization.edit"],
        rules: [
          ...ORGANIZATION_SKILL_READ_RULES,
          { action: "create", resource: "ORGANIZATION", subject: "SKILL" },
          { action: "update", resource: "ORGANIZATION", subject: "SKILL" },
        ],
      },
      {
        key: "organization.skills.delete",
        label: "Delete organization skills",
        description: "Allows users to delete organization skills.",
        requires: ["organization.skills.viewAndUse", "organization.edit"],
        rules: [...ORGANIZATION_SKILL_READ_RULES, { action: "delete", resource: "ORGANIZATION", subject: "SKILL" }],
      },
      {
        key: "organization.skills.manageAvailability",
        label: "Manage skill availability",
        description: "Allows users to enable and disable skills for the organization.",
        requires: ["organization.skills.viewAndUse", "organization.edit"],
        rules: [
          ...ORGANIZATION_SKILL_READ_RULES,
          { action: "create", resource: "ORGANIZATION", subject: "SKILL_OVERRIDE" },
          { action: "update", resource: "ORGANIZATION", subject: "SKILL_OVERRIDE" },
          { action: "delete", resource: "ORGANIZATION", subject: "SKILL_OVERRIDE" },
        ],
      },
      {
        key: "organization.reviewsPlaybooks.view",
        label: "View custom Playbooks and reviews",
        description: "Allows users to view custom Playbooks and reviews.",
        rules: ORG_TEMPLATE_READ_RULES,
      },
      {
        key: "organization.reviewsPlaybooks.createEdit",
        label: "Create/edit custom Playbooks and reviews",
        description: "Allows users to create, edit, duplicate, import, and export custom Playbooks and reviews.",
        requires: "organization.reviewsPlaybooks.view",
        rules: [
          ...ORG_TEMPLATE_READ_RULES,
          { action: "create", resource: "ORGANIZATION", subject: "ORG_TEMPLATE" },
          { action: "update", resource: "ORGANIZATION", subject: "ORG_TEMPLATE" },
        ],
      },
      {
        key: "organization.reviewsPlaybooks.delete",
        label: "Delete custom Playbooks and reviews",
        description: "Allows users to delete custom Playbooks and reviews.",
        requires: "organization.reviewsPlaybooks.view",
        rules: [...ORG_TEMPLATE_READ_RULES, { action: "delete", resource: "ORGANIZATION", subject: "ORG_TEMPLATE" }],
      },
    ],
  },
  {
    key: "teams",
    label: "Teams",
    heading: "Team level permissions",
    description: "Hand project creation and deletion power to team leads without opening up full org-level access.",
    groups: [
      { subheading: "Team management", toggleKeys: ["teams.view", "teams.editSettings"] },
      { subheading: "Project management", toggleKeys: ["teams.createProjects", "teams.deleteProjects"] },
    ],
    toggles: [
      {
        key: "teams.view",
        label: "View team",
        description: "Allows users to view teams and everything in them, including their projects and documents.",
        rules: [{ action: "read", resource: "TEAM" }],
      },
      {
        key: "teams.editSettings",
        label: "Edit team settings",
        description: "Allows users to modify existing team settings.",
        rules: [
          { action: "update", resource: "TEAM" },
          { action: "editTeamSettings", resource: "TEAM" },
        ],
      },
      {
        key: "teams.createProjects",
        label: "Create projects",
        description: "Allows users to create projects.",
        rules: [{ action: "create", resource: "PROJECT" }],
      },
      {
        key: "teams.deleteProjects",
        label: "Delete projects",
        description: "Allows users to delete projects.",
        rules: [{ action: "delete", resource: "PROJECT" }],
      },
    ],
  },
  {
    key: "projects",
    label: "Projects",
    heading: "Project level permissions",
    description:
      "Cover everything inside a project so project leads can manage their own work without escalating every action.",
    groups: [
      {
        subheading: "Project management",
        toggleKeys: ["projects.view", "projects.editSettings", "projects.createFieldLabels", "projects.archive"],
      },
      { subheading: "Client management", toggleKeys: ["clients.view", "clients.manage"] },
      {
        subheading: "Project actions",
        subgroups: [
          {
            subheading: "Reviews",
            toggleKeys: [
              "projects.reviewApplyRemove",
              "projects.reviewEdit",
              "projects.reviewView",
              "projects.reviewExport",
            ],
          },
          {
            subheading: "Playbooks",
            toggleKeys: [
              "projects.playbookApplyRemove",
              "projects.playbookEdit",
              "projects.playbookView",
              "projects.playbookExport",
            ],
          },
          {
            subheading: "Change orders",
            toggleKeys: ["projects.changeOrdersView", "projects.changeOrdersEdit", "projects.changeOrdersConnection"],
          },
        ],
      },
    ],
    toggles: [
      {
        key: "projects.view",
        label: "View project",
        description: "Allows users to view projects and their contents, including folders and documents.",
        rules: [{ action: "read", resource: "PROJECT" }],
      },
      {
        key: "projects.editSettings",
        label: "Edit project settings",
        description: "Allows users to modify project settings",
        rules: [
          { action: "update", resource: "PROJECT" },
          { action: "editProjectSettings", resource: "PROJECT" },
        ],
      },
      {
        key: "projects.archive",
        label: "Archive project",
        description: "Allows users to archive and restore projects.",
        rules: [
          { action: "archive", resource: "PROJECT" },
          { action: "read", resource: "PROJECT" },
        ],
      },
      {
        key: "projects.createFieldLabels",
        label: "Create project field labels",
        description: "Allows users to create new project detail field labels that populate the drop-down list.",
        // Five org-scoped lookup entities behind the project-detail
        // drop-downs; each has its own POST endpoint gated by create:<ENTITY>.
        // read is bundled so the toggle also unlocks listing the drop-down
        // options (GET /project-types etc. requires read:<ENTITY>). CLIENT is
        // deliberately absent — client visibility/creation is governed by the
        // clients.view / clients.manage toggles (and the organization.view cascade).
        rules: [
          { action: "read", resource: "PROJECT_TYPE" },
          { action: "create", resource: "PROJECT_TYPE" },
          { action: "read", resource: "OWNER" },
          { action: "create", resource: "OWNER" },
          { action: "read", resource: "ARCHITECT" },
          { action: "create", resource: "ARCHITECT" },
          { action: "read", resource: "DEPARTMENT" },
          { action: "create", resource: "DEPARTMENT" },
          { action: "read", resource: "REGION" },
          { action: "create", resource: "REGION" },
        ],
      },
      {
        key: "clients.view",
        label: "View clients",
        description: "Allows users to see a list of clients in project details.",
        rules: [{ action: "read", resource: "CLIENT" }],
      },
      {
        key: "clients.manage",
        label: "Manage clients",
        description: "Allows users to view, create, edit, or delete client labels in project details.",
        rules: [
          { action: "create", resource: "CLIENT" },
          { action: "read", resource: "CLIENT" },
          { action: "update", resource: "CLIENT" },
          { action: "delete", resource: "CLIENT" },
        ],
      },
      {
        key: "projects.reviewApplyRemove",
        label: "Apply/remove reviews",
        description: "Allows users to apply and remove project level reviews.",
        rules: [template("PROJECT", "create", "CHECKLIST"), template("PROJECT", "delete", "CHECKLIST")],
      },
      {
        key: "projects.reviewEdit",
        label: "Edit reviews",
        description: "Allows users to edit project level reviews.",
        rules: [template("PROJECT", "update", "CHECKLIST")],
      },
      {
        key: "projects.reviewView",
        label: "View reviews",
        description: "Allows users to view project level reviews.",
        rules: [template("PROJECT", "read", "CHECKLIST")],
      },
      {
        key: "projects.reviewExport",
        label: "Download reviews",
        description: "Allows users to download project level reviews.",
        rules: [template("PROJECT", "export", "CHECKLIST")],
      },
      {
        key: "projects.playbookApplyRemove",
        label: "Apply/remove Playbooks",
        description: "Allows users to apply and remove project level playbooks.",
        rules: [template("PROJECT", "create", "PLAYBOOK"), template("PROJECT", "delete", "PLAYBOOK")],
      },
      {
        key: "projects.playbookEdit",
        label: "Edit Playbooks",
        description: "Allows users to edit project level playbooks.",
        rules: [template("PROJECT", "update", "PLAYBOOK")],
      },
      {
        key: "projects.playbookView",
        label: "View Playbooks",
        description: "Allows users to view project level playbooks.",
        rules: [template("PROJECT", "read", "PLAYBOOK")],
      },
      {
        key: "projects.playbookExport",
        label: "Download Playbooks",
        description: "Allows users to download project level playbooks.",
        rules: [template("PROJECT", "export", "PLAYBOOK")],
      },
      {
        key: "projects.changeOrdersView",
        label: "View change orders",
        description:
          "Allows users to view change orders, including their source, analysis, activity log, comments, and drafts.",
        requires: "comments.view",
        rules: [...changeOrderRules("read"), ...changeOrderSubjectRules("read", "SOURCE_RECORD")],
      },
      {
        key: "projects.changeOrdersEdit",
        label: "Edit change orders",
        description:
          "Allows users to confirm and deny change orders, assign tasks, comment, and draft and push change orders to the ERP.",
        requires: ["projects.changeOrdersView", "tasks.view", "tasks.create", "comments.create"],
        rules: [...changeOrderRules("read"), ...changeOrderRules("update")],
      },
      {
        key: "projects.changeOrdersConnection",
        label: "Connect/disconnect ProjectSight",
        description: "Allows users to connect and disconnect the ProjectSight integration in project settings.",
        requires: "projects.editSettings",
        rules: [
          ...changeOrderSubjectRules("read", "INTEGRATION"),
          ...changeOrderSubjectRules("update", "INTEGRATION"),
          ...changeOrderSubjectRules("delete", "INTEGRATION"),
        ],
      },
    ],
  },
  {
    key: "folders",
    label: "Folders",
    heading: "Folder level permissions",
    subheading: "Folder management",
    description: "Let teams organize documents into folders that fit their own workflow.",
    toggles: [
      {
        key: "folders.view",
        label: "View folders",
        description: "Allows users to view folders.",
        rules: [{ action: "read", resource: "FOLDER" }],
      },
      {
        key: "folders.create",
        label: "Create folders",
        description: "Allows users to create folders.",
        requires: "folders.view",
        rules: [{ action: "create", resource: "FOLDER" }],
      },
      {
        key: "folders.edit",
        label: "Edit folders",
        description: "Allows users to edit folder details and move folders to a different location.",
        requires: "folders.view",
        rules: [
          { action: "update", resource: "FOLDER" },
          { action: "move", resource: "FOLDER" },
        ],
      },
      {
        key: "folders.delete",
        label: "Delete folders",
        description: "Allows users to delete folders.",
        requires: "folders.view",
        rules: [{ action: "delete", resource: "FOLDER" }],
      },
      {
        key: "folders.archive",
        label: "Archive folders",
        description: "Allows users to archive and restore folders.",
        requires: "folders.view",
        rules: [
          { action: "archive", resource: "FOLDER" },
          { action: "read", resource: "FOLDER" },
        ],
      },
    ],
  },
  {
    key: "documents",
    label: "Documents",
    heading: "Document level permissions",
    description: "Control who can upload, move, rename, delete, and download files.",
    groups: [
      {
        subheading: "Document management",
        toggleKeys: [
          "documents.view",
          "documents.create",
          "documents.edit",
          "documents.delete",
          "documents.download",
          "documents.archive",
        ],
      },
      {
        subheading: "Document actions",
        subgroups: [
          {
            subheading: "Reviews",
            toggleKeys: [
              "documents.reviewApplyRemove",
              "documents.reviewEdit",
              "documents.reviewView",
              "documents.reviewExport",
            ],
          },
          {
            subheading: "Playbooks",
            toggleKeys: [
              "documents.playbookApplyRemove",
              "documents.playbookEdit",
              "documents.playbookView",
              "documents.playbookExport",
            ],
          },
          {
            subheading: "Redlining",
            toggleKeys: ["documents.redline"],
          },
        ],
      },
    ],
    toggles: [
      {
        key: "documents.view",
        label: "View documents",
        description: "Allows users to view documents.",
        rules: [{ action: "read", resource: "FILE" }],
      },
      {
        key: "documents.create",
        label: "Create/upload documents",
        description: "Allows users to create and upload new documents, including setting the document version.",
        requires: "documents.view",
        rules: [
          { action: "create", resource: "FILE" },
          { action: "upload", resource: "FILE" },
          { action: "read", resource: "FILE" },
        ],
      },
      {
        key: "documents.edit",
        label: "Edit documents",
        description: "Allows users to edit documents, including moving and renaming them.",
        requires: "documents.view",
        rules: [
          { action: "update", resource: "FILE" },
          { action: "read", resource: "FILE" },
        ],
      },
      {
        key: "documents.delete",
        label: "Delete documents",
        description: "Allows users to delete documents.",
        requires: "documents.view",
        rules: [
          { action: "delete", resource: "FILE" },
          { action: "read", resource: "FILE" },
        ],
      },
      {
        key: "documents.download",
        label: "Download document",
        description: "Allows users to download documents.",
        requires: "documents.view",
        rules: [{ action: "download", resource: "FILE" }],
      },
      {
        key: "documents.archive",
        label: "Archive document",
        description: "Allows users to archive and unarchive documents.",
        requires: "documents.view",
        rules: [
          { action: "archive", resource: "FILE" },
          { action: "read", resource: "FILE" },
        ],
      },
      {
        key: "documents.reviewApplyRemove",
        label: "Apply/remove reviews",
        description: "Allows users to apply and remove document level reviews.",
        rules: [template("FILE", "create", "CHECKLIST"), template("FILE", "delete", "CHECKLIST")],
      },
      {
        key: "documents.reviewEdit",
        label: "Edit reviews",
        description: "Allows users to edit document level reviews.",
        rules: [template("FILE", "update", "CHECKLIST")],
      },
      {
        key: "documents.reviewView",
        label: "View reviews",
        description: "Allows users to view document level reviews.",
        rules: [template("FILE", "read", "CHECKLIST")],
      },
      {
        key: "documents.reviewExport",
        label: "Download reviews",
        description: "Allows users to download document level reviews.",
        rules: [template("FILE", "export", "CHECKLIST")],
      },
      {
        key: "documents.playbookApplyRemove",
        label: "Apply/remove Playbooks",
        description: "Allows users to apply and remove document level playbooks.",
        rules: [template("FILE", "create", "PLAYBOOK"), template("FILE", "delete", "PLAYBOOK")],
      },
      {
        key: "documents.playbookEdit",
        label: "Edit Playbooks",
        description: "Allows users to edit document level playbooks.",
        rules: [template("FILE", "update", "PLAYBOOK")],
      },
      {
        key: "documents.playbookView",
        label: "View Playbooks",
        description: "Allows users to view document level playbooks.",
        rules: [template("FILE", "read", "PLAYBOOK")],
      },
      {
        key: "documents.playbookExport",
        label: "Download Playbooks",
        description: "Allows users to download document level playbooks.",
        rules: [template("FILE", "export", "PLAYBOOK")],
      },
      {
        key: "documents.redline",
        label: "Redline",
        description: "Apply suggested edits to a contract, edit and export to Word or PDF.",
        rules: [
          { action: "create", resource: "FILE", subject: "FILE_REDLINE" },
          { action: "update", resource: "FILE", subject: "FILE_REDLINE" },
          { action: "export", resource: "FILE", subject: "FILE_REDLINE" },
        ],
      },
    ],
  },
  {
    key: "projectAssist",
    label: "Project Assist & Chat",
    heading: "Project Assist & Chat",
    subheading: "Chat management",
    description: "Control who can chat with project documents.",
    toggles: [
      {
        key: "projectAssist.create",
        label: "Create",
        description: "Create chat threads.",
        rules: [
          { action: "read", resource: "THREAD" },
          { action: "create", resource: "THREAD" },
        ],
      },
      {
        key: "projectAssist.delete",
        label: "Delete",
        description: "Delete chat threads.",
        rules: [
          { action: "read", resource: "THREAD" },
          { action: "delete", resource: "THREAD" },
        ],
      },
      {
        key: "projectAssist.view",
        label: "View chat thread content",
        description: "Allows users to view chat threads.",
        rules: [{ action: "read", resource: "THREAD" }],
      },
      {
        key: "projectAssist.edit",
        label: "Edit chat threads",
        description: "This includes renaming chat threads.",
        rules: [
          { action: "read", resource: "THREAD" },
          { action: "update", resource: "THREAD" },
        ],
      },
    ],
  },
  {
    key: "commentsMarkups",
    label: "Comments & Markups",
    heading: "Comments & Markups",
    description: "Control who can comment and markup anywhere they're supported.",
    groups: [
      {
        subheading: "Comments",
        toggleKeys: ["comments.view", "comments.create", "comments.edit", "comments.delete"],
      },
      {
        subheading: "Markups",
        toggleKeys: ["markups.view", "markups.create", "markups.edit", "markups.delete"],
      },
    ],
    toggles: [
      {
        key: "comments.view",
        label: "View comments",
        description: "Allows users to view comments.",
        rules: commentRules("read"),
      },
      {
        key: "comments.create",
        label: "Create comments",
        description: "Allows users to create comments.",
        rules: [...commentRules("read"), ...commentRules("create")],
      },
      {
        key: "comments.edit",
        label: "Edit comments",
        description: "Allows users to edit comments.",
        rules: [...commentRules("read"), ...commentRules("update")],
      },
      {
        key: "comments.delete",
        label: "Delete comments",
        description: "Allows users to delete comments.",
        rules: [...commentRules("read"), ...commentRules("delete")],
      },
      {
        key: "markups.view",
        label: "View markups",
        description: "Allows users to view markups.",
        rules: markupRules("read"),
      },
      {
        key: "markups.create",
        label: "Create markups",
        description: "Allows users to create markups including textbox, note, drawing, highlight.",
        rules: [...markupRules("read"), ...markupRules("create")],
      },
      {
        key: "markups.edit",
        label: "Edit markups",
        description: "Allows users to edit markups.",
        rules: [...markupRules("read"), ...markupRules("update")],
      },
      {
        key: "markups.delete",
        label: "Delete markups",
        description: "Allows users to delete markups.",
        rules: [...markupRules("read"), ...markupRules("delete")],
      },
    ],
  },
  {
    key: "tasks",
    label: "Tasks",
    heading: "Tasks",
    subheading: "Task management",
    description: "Let managers assign work and track progress.",
    toggles: [
      {
        key: "tasks.view",
        label: "View tasks",
        description: "Allows users to view tasks.",
        rules: taskRules("read"),
      },
      {
        key: "tasks.create",
        label: "Create tasks",
        description: "Allows users to create tasks.",
        rules: [...taskRules("read"), ...taskRules("create")],
      },
      {
        key: "tasks.edit",
        label: "Edit tasks",
        description: "Allows users to edit tasks.",
        rules: [...taskRules("read"), ...taskRules("update")],
      },
      {
        key: "tasks.delete",
        label: "Delete tasks",
        description: "Allows users to delete tasks.",
        rules: [...taskRules("read"), ...taskRules("delete")],
      },
    ],
  },
  {
    key: "workflows",
    label: "Workflows",
    heading: "Workflows",
    description: "Control who can build workflow templates and apply, advance, and remove workflows on documents.",
    groups: [
      {
        subheading: "Workflow Templates",
        toggleKeys: [
          "workflows.templateView",
          "workflows.templateCreate",
          "workflows.templateEdit",
          "workflows.templateDelete",
        ],
      },
      {
        subheading: "Applied Workflows",
        toggleKeys: [
          "workflows.applicationView",
          "workflows.applicationApply",
          "workflows.applicationEdit",
          "workflows.applicationRemove",
        ],
      },
    ],
    toggles: [
      {
        key: "workflows.templateView",
        label: "View workflow templates",
        description: "Allows users to view workflow templates.",
        rules: workflowRules(TEMPLATE_TIERS, TEMPLATE_READS),
      },
      {
        key: "workflows.templateCreate",
        label: "Create workflow templates",
        description: "Allows users to create workflow templates.",
        rules: workflowRules(TEMPLATE_TIERS, [
          ...TEMPLATE_READS,
          ["create", "WORKFLOW_TEMPLATE"],
          ["create", "WORKFLOW_TEMPLATE_STEP"],
        ]),
      },
      {
        key: "workflows.templateEdit",
        label: "Edit workflow templates",
        description: "Allows users to rename workflow templates and add, edit, reorder, or remove their steps.",
        rules: workflowRules(TEMPLATE_TIERS, [
          ...TEMPLATE_READS,
          ["update", "WORKFLOW_TEMPLATE"],
          ["update", "WORKFLOW_TEMPLATE_STEP"],
          ["create", "WORKFLOW_TEMPLATE_STEP"],
          ["delete", "WORKFLOW_TEMPLATE_STEP"],
        ]),
      },
      {
        key: "workflows.templateDelete",
        label: "Delete workflow templates",
        description: "Allows users to delete workflow templates.",
        rules: workflowRules(TEMPLATE_TIERS, [
          ...TEMPLATE_READS,
          ["delete", "WORKFLOW_TEMPLATE"],
          ["delete", "WORKFLOW_TEMPLATE_STEP"],
        ]),
      },
      {
        key: "workflows.applicationView",
        label: "View applied workflows",
        description: "Allows users to view workflows applied to documents.",
        requires: TASK_DEPENDENCIES,
        rules: workflowRules(APPLICATION_TIERS, APPLICATION_READS),
      },
      {
        key: "workflows.applicationApply",
        label: "Apply workflows",
        description: "Allows users to apply a workflow template to documents.",
        requires: [...TASK_DEPENDENCIES, "workflows.templateView"],
        rules: workflowRules(APPLICATION_TIERS, [...APPLICATION_READS, ["create", "WORKFLOW_APPLICATION"]]),
      },
      {
        key: "workflows.applicationEdit",
        label: "Edit and advance applied workflows",
        description:
          "Allows users to mark steps done, skip, reopen, reassign, and poke, and to add or remove steps on a running workflow.",
        requires: TASK_DEPENDENCIES_WITH_DELETE,
        rules: workflowRules(APPLICATION_TIERS, [
          ...APPLICATION_READS,
          ["update", "WORKFLOW_APPLICATION"],
          ["update", "WORKFLOW_APPLICATION_STEP"],
          ["create", "WORKFLOW_APPLICATION_STEP"],
          ["delete", "WORKFLOW_APPLICATION_STEP"],
        ]),
      },
      {
        key: "workflows.applicationRemove",
        label: "Remove applied workflows",
        description: "Allows users to remove a workflow from documents.",
        requires: TASK_DEPENDENCIES_WITH_DELETE,
        rules: workflowRules(APPLICATION_TIERS, [...APPLICATION_READS, ["delete", "WORKFLOW_APPLICATION"]]),
      },
    ],
  },
];

/** Flattened list of every toggle across categories. */
export const PERMISSION_TOGGLES: PermissionToggle[] = PERMISSION_CATALOG.flatMap((category) => category.toggles);

/** Lookup a toggle by its stable key. */
export const PERMISSION_TOGGLES_BY_KEY: Record<string, PermissionToggle> = Object.fromEntries(
  PERMISSION_TOGGLES.map((toggle) => [toggle.key, toggle]),
);

export interface PermissionCatalogVisibility {
  organizationSkillsEnabled: boolean;
  changeOrdersEnabled: boolean;
  customReviewsPlaybooksEnabled: boolean;
}

/** Whether a toggle belongs to a feature the org can't reach, so it's hidden from the picker and the permissions chart. */
export function isHiddenPermissionToggleKey(key: string, visibility: PermissionCatalogVisibility): boolean {
  return (
    (!visibility.organizationSkillsEnabled && key.startsWith("organization.skills.")) ||
    (!visibility.changeOrdersEnabled && key.startsWith("projects.changeOrders")) ||
    (!visibility.customReviewsPlaybooksEnabled && key.startsWith(REVIEWS_PLAYBOOKS_TOGGLE_PREFIX))
  );
}

/** PERMISSION_CATALOG with gated toggles — and the groups/subgroups that only hold them — removed. */
export function getVisiblePermissionCatalog(visibility: PermissionCatalogVisibility): PermissionCategory[] {
  return PERMISSION_CATALOG.map((category) => {
    let visibleCategory = category;
    if (!visibility.organizationSkillsEnabled && category.key === "organization") {
      visibleCategory = {
        ...visibleCategory,
        groups: visibleCategory.groups?.filter((group) => group.subheading !== "Organization skills"),
        toggles: visibleCategory.toggles.filter((toggle) => !toggle.key.startsWith("organization.skills.")),
      };
    }
    if (!visibility.customReviewsPlaybooksEnabled && category.key === "organization") {
      visibleCategory = {
        ...visibleCategory,
        groups: visibleCategory.groups?.filter((group) => group.subheading !== REVIEWS_PLAYBOOKS_SUBHEADING),
        toggles: visibleCategory.toggles.filter((toggle) => !toggle.key.startsWith(REVIEWS_PLAYBOOKS_TOGGLE_PREFIX)),
      };
    }
    if (!visibility.changeOrdersEnabled && category.key === "projects") {
      visibleCategory = {
        ...visibleCategory,
        groups: visibleCategory.groups?.map((group) =>
          group.subgroups
            ? { ...group, subgroups: group.subgroups.filter((subgroup) => subgroup.subheading !== "Change orders") }
            : group,
        ),
        toggles: visibleCategory.toggles.filter((toggle) => !toggle.key.startsWith("projects.changeOrders")),
      };
    }
    return visibleCategory;
  });
}

// Resolving which toggles a role's raw permission rows satisfy requires
// replicating the API's cascade + manage-wildcard + DENY-override semantics
// (ENTITY_HIERARCHY in casl-ability.service.ts). That resolution now happens
// server-side — GET /roles/:id returns an already-resolved `toggleKeys: string[]`
// (apps/api/apps/api/src/resources/roles/role-permission-catalog.ts) — so the
// picker just uses that directly instead of reinterpreting raw rules here.

/** The category's tier-read toggle key (`${category.key}.view`), or null if the category has none. */
export function viewToggleKeyForCategory(category: PermissionCategory): string | null {
  const key = `${category.key}.view`;
  return category.toggles.some((toggle) => toggle.key === key) ? key : null;
}

/**
 * Subgroup-scoped view toggles whose siblings are narrower than a whole category, so
 * viewToggleKeyForCategory/cascadeViewToggleOff's category-wide sweep can't cover them (that
 * would also wipe unrelated toggles in the rest of the category). Mirrors the same on/off
 * relationship the category-level mechanism already provides for organization.view/teams.view/etc.,
 * just at subgroup scope instead of whole-category.
 */
const VIEW_TOGGLE_SIBLING_GROUPS: Record<string, string[]> = {
  "documents.reviewView": ["documents.reviewApplyRemove", "documents.reviewEdit", "documents.reviewExport"],
  "documents.playbookView": ["documents.playbookApplyRemove", "documents.playbookEdit", "documents.playbookExport"],
  "projects.reviewView": ["projects.reviewApplyRemove", "projects.reviewEdit", "projects.reviewExport"],
  "projects.playbookView": ["projects.playbookApplyRemove", "projects.playbookEdit", "projects.playbookExport"],
  "projects.changeOrdersView": ["projects.changeOrdersEdit"],
  "comments.view": ["comments.create", "comments.edit", "comments.delete"],
  "markups.view": ["markups.create", "markups.edit", "markups.delete"],
  "clients.view": ["clients.manage"],
  "workflows.templateView": ["workflows.templateCreate", "workflows.templateEdit", "workflows.templateDelete"],
  "workflows.applicationView": [
    "workflows.applicationApply",
    "workflows.applicationEdit",
    "workflows.applicationRemove",
  ],
};

/** Org toggles that grant their own feature access, so they neither need nor follow organization.view. */
function isIndependentOfCategoryView(key: string): boolean {
  return key.startsWith("organization.skills.") || key.startsWith(REVIEWS_PLAYBOOKS_TOGGLE_PREFIX);
}

/** A toggle's `requires` targets, normalized to a list. */
function requiredKeysFor(key: string): string[] {
  const required = PERMISSION_TOGGLES_BY_KEY[key]?.requires;
  if (required === undefined) {
    return [];
  }
  return typeof required === "string" ? [required] : required;
}

/**
 * Cross-category prerequisites between view toggles: folders and documents only exist inside a
 * project, so neither can be viewed without projects.view. Applied after the sibling passes so
 * that anything forcing documents.view/folders.view on transitively forces projects.view on too,
 * and switching projects.view off cascades through both dependent categories.
 */
const VIEW_TOGGLE_PREREQUISITES: Record<string, string> = {
  "documents.view": "projects.view",
  "folders.view": "projects.view",
};

/**
 * Returns a new set with every dependency-implied toggle added: category view keys, subgroup
 * view keys (VIEW_TOGGLE_SIBLING_GROUPS), `requires` targets, and view-key prerequisites
 * (VIEW_TOGGLE_PREREQUISITES). Repeats to a fixpoint, so a key forced on by one rule still
 * picks up its own dependencies. Never removes keys.
 */
export function enforceViewToggleDependencies(selectedKeys: ReadonlySet<string>): Set<string> {
  const result = new Set(selectedKeys);
  let changed = true;
  while (changed) {
    changed = false;
    for (const category of PERMISSION_CATALOG) {
      const viewKey = viewToggleKeyForCategory(category);
      if (viewKey === null || result.has(viewKey)) {
        continue;
      }
      if (
        category.toggles.some(
          (toggle) => toggle.key !== viewKey && !isIndependentOfCategoryView(toggle.key) && result.has(toggle.key),
        )
      ) {
        result.add(viewKey);
        changed = true;
      }
    }
    for (const [viewKey, siblings] of Object.entries(VIEW_TOGGLE_SIBLING_GROUPS)) {
      if (!result.has(viewKey) && siblings.some((sibling) => result.has(sibling))) {
        result.add(viewKey);
        changed = true;
      }
    }
    for (const key of [...result]) {
      for (const required of requiredKeysFor(key)) {
        if (!result.has(required)) {
          result.add(required);
          changed = true;
        }
      }
    }
  }
  for (const [viewKey, prerequisite] of Object.entries(VIEW_TOGGLE_PREREQUISITES)) {
    if (result.has(viewKey)) {
      result.add(prerequisite);
    }
  }
  return result;
}

/**
 * The off-direction counterpart of `enforceViewToggleDependencies`. Removes `key` and whatever
 * it gates: a category view key takes its whole category, a subgroup view key
 * (VIEW_TOGGLE_SIBLING_GROUPS) takes only its siblings, a prerequisite
 * (VIEW_TOGGLE_PREREQUISITES) takes its dependent view keys, and anything that `requires`
 * something removed here goes too, to a fixpoint. Dependencies are one-directional: removing a
 * dependent never removes its target.
 */
export function cascadeViewToggleOff(selectedKeys: ReadonlySet<string>, key: string): Set<string> {
  let result = new Set(selectedKeys);
  const removed = new Set([key]);
  const category = PERMISSION_CATALOG.find((candidate) => viewToggleKeyForCategory(candidate) === key);
  if (category !== undefined) {
    for (const toggle of category.toggles) {
      if (!isIndependentOfCategoryView(toggle.key)) {
        removed.add(toggle.key);
      }
    }
  }
  for (const sibling of VIEW_TOGGLE_SIBLING_GROUPS[key] ?? []) {
    removed.add(sibling);
  }
  let changed = true;
  while (changed) {
    changed = false;
    for (const selected of result) {
      if (removed.has(selected)) {
        continue;
      }
      if (requiredKeysFor(selected).some((required) => removed.has(required))) {
        removed.add(selected);
        changed = true;
      }
    }
  }
  for (const gone of removed) {
    result.delete(gone);
  }
  for (const [dependentViewKey, prerequisite] of Object.entries(VIEW_TOGGLE_PREREQUISITES)) {
    if (prerequisite === key) {
      result = cascadeViewToggleOff(result, dependentViewKey);
    }
  }
  return result;
}
